import { useEffect, useState } from 'react'
import DevLog from '../../../modules/devlog/Module'

// A hung request must not hold a block behind its loader forever.
const LOAD_TIMEOUT_MS = 10000

// Only images that did load: going back to a block already seen shows it at once, while a failed
// image is requested again on the next mount.
const loaded = new Set<string>()

const loadImage = (url: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      loaded.add(url)
      resolve()
    }
    image.onerror = () => reject(new Error(`Failed to load ${url}`))
    image.src = url
  })

// True while any of the images is still loading, false once every one of them has loaded, failed or
// run out of time. Blocks do not call it directly: they go through `AssetsGate`, which adds the delay
// and the minimum time on screen to the store's loader. Pass the same URLs the block renders, so the
// browser serves them from its cache.
export default function useAssetsLoading(urls: Array<string | null | undefined>): boolean {
  const unique = [...new Set(urls.filter(Boolean))]
  const key = unique.join('\n')
  const [doneKey, setDoneKey] = useState<string>(null)
  const ready = doneKey === key || unique.every(url => loaded.has(url))

  useEffect(() => {
    const pending = unique.filter(url => !loaded.has(url))
    if (!pending.length) return

    let alive = true
    let timer: ReturnType<typeof setTimeout>
    const timeout = new Promise<void>(resolve => {
      timer = setTimeout(() => {
        DevLog.error(
          new Error('Image load timeout'),
          'SW[Ecommerce]:',
          'useAssetsLoading',
          pending.filter(url => !loaded.has(url))
        )
        resolve()
      }, LOAD_TIMEOUT_MS)
    })
    const loads = Promise.allSettled(pending.map(loadImage)).then(results =>
      results.forEach(result => {
        if ('rejected' === result.status) {
          DevLog.error(result.reason, 'SW[Ecommerce]:', 'useAssetsLoading')
        }
      })
    )

    void Promise.race([loads, timeout]).then(() => {
      clearTimeout(timer)
      if (alive) setDoneKey(key)
    })
    return () => {
      alive = false
      clearTimeout(timer)
    }
    // `unique` is derived from `key`; the key alone decides when to load again.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return !ready
}
